import { AfterContentInit, Component, Input } from '@angular/core'
import { Router } from '@angular/router'
import { addDays, differenceInDays, parseISO } from 'date-fns'
import { ReplaySubject } from 'rxjs'
import {
    AvailabilityType,
    Criteria,
    RecordListingVisitGQL,
    SearchListing,
    SearchListingsQuery,
    SearchListingStatus
} from '../../../../graphql'
import { searchListingsCriteriaKey } from '../../../constant'
import { Amplitude } from '../../../injectable'
import { toLocalDate, UnsubscribeOnDestroy } from '../../../utility'
import { SearchMetadata } from '../search-metadata'

@Component({
    selector: 'app-search-listing',
    templateUrl: 'search-listing.html'
})
export class SearchListingComponent extends UnsubscribeOnDestroy implements AfterContentInit {
    
    @Input() index: number
    @Input() searchListing: SearchListingsQuery['searchListings']['results'][0] | SearchListing
    @Input() checkin: string
    @Input() checkout: string
    @Input() minimumConsecutiveNights: number
    @Input() searchMetadata$ = new ReplaySubject<SearchMetadata>(1)
    @Input() consumerView = false

    coverImageUrl: string | undefined | null
    searchMetadata: SearchMetadata
    nights: number | undefined

    constructor(private _amplitude: Amplitude,
                private _recordListingVisit: RecordListingVisitGQL,
                private _router: Router) {
        super()
    }

    ngAfterContentInit() {
        this._subscriptions.push(this.searchMetadata$.subscribe(searchMetadata => {
            this.searchMetadata = searchMetadata
        }))

        const coverPhoto = this.searchListing.summary.photos.find(photo => photo.useAsCover) || this.searchListing.summary.photos[0]
        this.coverImageUrl = coverPhoto?.url
        if (this.checkin && this.checkout) {
            this.nights = differenceInDays(parseISO(this.checkout), parseISO(this.checkin))
        }
    }

    openInNewTab() {
        this._subscriptions.push(this._recordListingVisit.mutate({
            input: {
                listingId: this.searchListing.listingId,
                checkin: parseISO(this.checkin),
                checkout: parseISO(this.checkout),
                searchId: this.searchMetadata?.searchId,
                pageRank: this.searchMetadata?.pageRank(this.searchListing.listingId),
                pageNumber: this.searchMetadata?.pageNumber,
                totalResults: this.searchMetadata?.totalResults,
            }
        }).subscribe())

        if (this.isComingSoon()) {
            return
        }

        const url = this.checkin && this.checkout
            ? this._router.serializeUrl(this._router.createUrlTree(['/listing', this.searchListing.listingId, this.checkin, this.checkout]))
            : this._router.serializeUrl(this._router.createUrlTree(['/listing', this.searchListing.listingId]))
        window.open(url, '_blank')
    }

    isComingSoon() {
        return this.searchListing?.status === SearchListingStatus.ComingSoon
    }

    isAvailableOtherDates() {
        return this.searchListing?.status === SearchListingStatus.AvailableOtherDates
    }

    isAvailable() {
        return this.searchListing?.status === SearchListingStatus.Available
    }

    isRental() {
        return this.searchListing?.availabilityType == AvailabilityType.Rental
    }

    isExchange() {
        return !this.isRental()
    }

    deepLinkToAvailableFrom($event: any) {
        if (!this.minimumConsecutiveNights) { return true }

        const derivedCheckin: Date = parseISO(this.searchListing?.availableFrom?.start)
        const derivedCheckout: Date = addDays(derivedCheckin, this.minimumConsecutiveNights)
        const deepLink = ['/listing', this.searchListing.listingId, toLocalDate(derivedCheckin), toLocalDate(derivedCheckout)]

        this._amplitude.logEvent('Search Available From Clicked', {
            listingId: this.searchListing?.listingId,
            checkin: toLocalDate(derivedCheckin),
            checkout: toLocalDate(derivedCheckout)
        })

        // TODO: Use behavior subject to pass criteria around? So that only one component knows about session storage
        const criteriaRaw: string | null = sessionStorage.getItem(searchListingsCriteriaKey)
        if (criteriaRaw) {
            const criteria: Criteria = JSON.parse(criteriaRaw)
            criteria.datesCriterion = {
                checkin: toLocalDate(derivedCheckin),
                checkout: toLocalDate(derivedCheckout)
            }
            sessionStorage.setItem(searchListingsCriteriaKey, JSON.stringify(criteria))
        }

        this._router.navigate(deepLink)
        $event.stopPropagation()
        $event.preventDefault()
        return false
    }
}
